/**
 * Instrument Transposition & Multi-Musician Band Coordination Utility (Frontend ESM)
 * Handles transposing instruments (Bb, Eb, F), capos, and concert pitch calculation.
 */

import { transposeNote, transposeChord, transposeChordTokens, transposeHtmlChords } from './chordTransposer.js';

export const INSTRUMENT_PROFILES = {
  CONCERT_C: { id: 'concert_c', name: 'Concert Pitch (C)', offset: 0, family: 'C', supportsCapo: false, description: 'Piano, Teclado, Violão, Baixo, Voz' },
  GUITAR_STANDARD: { id: 'guitar_standard', name: 'Violão / Guitarra (C)', offset: 0, family: 'C', supportsCapo: true, description: 'Violão e Guitarra padrão' },
  BASS: { id: 'bass', name: 'Baixo (C)', offset: 0, family: 'C', supportsCapo: false, description: 'Contrabaixo elétrico / acústico' },
  KEYBOARD: { id: 'keyboard', name: 'Teclado / Piano (C)', offset: 0, family: 'C', supportsCapo: false, description: 'Teclado, Piano, Órgão' },

  TENOR_SAX_BB: { id: 'tenor_sax_bb', name: 'Saxofone Tenor (Bb)', offset: 2, family: 'Bb', supportsCapo: false, description: 'Sax Tenor (+2 semitons / 1 tom acima)' },
  TRUMPET_BB: { id: 'trumpet_bb', name: 'Trompete (Bb)', offset: 2, family: 'Bb', supportsCapo: false, description: 'Trompete em Si bemol' },
  CLARINET_BB: { id: 'clarinet_bb', name: 'Clarinete (Bb)', offset: 2, family: 'Bb', supportsCapo: false, description: 'Clarinete em Si bemol' },
  SOPRANO_SAX_BB: { id: 'soprano_sax_bb', name: 'Saxofone Soprano (Bb)', offset: 2, family: 'Bb', supportsCapo: false, description: 'Sax Soprano' },

  ALTO_SAX_EB: { id: 'alto_sax_eb', name: 'Saxofone Alto (Eb)', offset: 9, family: 'Eb', supportsCapo: false, description: 'Sax Alto (+9 semitons / 3 semitons abaixo)' },
  BARITONE_SAX_EB: { id: 'baritone_sax_eb', name: 'Saxofone Barítono (Eb)', offset: 9, family: 'Eb', supportsCapo: false, description: 'Sax Barítono em Mi bemol' },

  FRENCH_HORN_F: { id: 'french_horn_f', name: 'Trompa (F)', offset: 7, family: 'F', supportsCapo: false, description: 'Trompa em Fá (+7 semitons)' }
};

export function getInstrumentProfile(id) {
  if (!id) return INSTRUMENT_PROFILES.CONCERT_C;
  return Object.values(INSTRUMENT_PROFILES).find(p => p.id === id) || INSTRUMENT_PROFILES.CONCERT_C;
}

/**
 * Somente instrumentos de corda dedilhada (violão, guitarra, banjo, ukulele, etc.)
 * aceitam capotraste. Usado p/ mostrar/esconder o seletor de capo no stage.
 */
export function instrumentSupportsCapo(id) {
  return getInstrumentProfile(id).supportsCapo === true;
}

/**
 * Calculates effective semitone shift that a musician sees on stage.
 * Formula: Effective Semitone = Shared Band Semitone + Instrument Offset - Capo Fret + Personal Offset
 */
export function calculateEffectiveSemitone({
  sharedSemitone = 0,
  instrument = 'concert_c',
  instrumentOffset = null,
  capo = 0,
  personalOffset = 0
} = {}) {
  let resolvedInstrumentOffset = 0;
  if (typeof instrumentOffset === 'number' && !isNaN(instrumentOffset)) {
    resolvedInstrumentOffset = instrumentOffset;
  } else if (typeof instrument === 'string') {
    resolvedInstrumentOffset = getInstrumentProfile(instrument).offset;
  }

  const resolvedCapoOffset = (typeof capo === 'number' && !isNaN(capo)) ? -capo : 0;
  const resolvedPersonal = (typeof personalOffset === 'number' && !isNaN(personalOffset)) ? personalOffset : 0;
  const resolvedShared = (typeof sharedSemitone === 'number' && !isNaN(sharedSemitone)) ? sharedSemitone : 0;

  return resolvedShared + resolvedInstrumentOffset + resolvedCapoOffset + resolvedPersonal;
}

export { transposeNote, transposeChord, transposeChordTokens, transposeHtmlChords };
